import { t } from '@lingui/macro'
import { IconButton, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip } from '@mui/material'
import { ContactLogResponse } from '@om1/aspen-api/codegen/client/models/ContactLogResponse'
import { formatDateTimeET } from '@om1/platform-components'
import { Pencil } from 'lucide-react'
import React, { useMemo } from 'react'
import { getAttemptOutcomeDisplay, getContactTypeDisplay } from './LogAttemptInput'
import { TruncateText } from './TruncateText'

interface LogAttemptTableProps {
    attempts: ContactLogResponse[]
    /** Callback when edit button is clicked */
    onEdit?: (attempt: ContactLogResponse) => void
}

export const LogAttemptTable: React.FC<LogAttemptTableProps> = ({ attempts, onEdit }) => {
    const sortedAttempts = useMemo(() => {
        return [...attempts].sort((a, b) => {
            return new Date(b.createdDttm).getTime() - new Date(a.createdDttm).getTime()
        })
    }, [attempts])

    // Check if attempt is within 24-hour edit window
    const isEditable = (attempt: ContactLogResponse): boolean => {
        const createdTime = new Date(attempt.createdDttm).getTime()
        const now = Date.now()
        const hoursSinceCreation = (now - createdTime) / (1000 * 60 * 60)
        return hoursSinceCreation <= 24
    }

    return (
        <TableContainer
            component={Paper}
            sx={{
                height: '100%',
                overflow: 'auto',
                boxShadow: 'none',
                '& .MuiTable-root': {
                    borderCollapse: 'separate',
                    borderSpacing: 0
                },
                '& .MuiTableBody-root': {
                    height: '100%'
                },
                '& .MuiTableCell-root': {
                    borderBottom: '1px solid rgba(224, 224, 224, 1)'
                },
                '& .MuiTableHead-root .MuiTableCell-root': {
                    fontWeight: 600,
                    color: '#374151'
                }
            }}
        >
            <Table stickyHeader>
                <TableHead>
                    <TableRow>
                        <TableCell>{t`Date`}</TableCell>
                        <TableCell>{t`Contact Type`}</TableCell>
                        <TableCell>{t`Outcome`}</TableCell>
                        <TableCell>{t`Notes`}</TableCell>
                        <TableCell>{t`Next Contact`}</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {sortedAttempts.map((attempt) => {
                        const editable = isEditable(attempt)
                        return (
                            <TableRow key={attempt.id}>
                                <TableCell>{formatDateTimeET(attempt.createdDttm)}</TableCell>
                                <TableCell>{getContactTypeDisplay(attempt.contactType)}</TableCell>
                                <TableCell>{getAttemptOutcomeDisplay(attempt.attemptOutcome)}</TableCell>
                                <TableCell>
                                    <TruncateText text={attempt.notes || ''} />
                                </TableCell>
                                <TableCell>
                                    <div className='flex items-center gap-2'>
                                        <span>{attempt.suggestedNextContactTime ? formatDateTimeET(attempt.suggestedNextContactTime) : t`-`}</span>
                                        {onEdit && editable && (
                                            <Tooltip title={t`Edit`}>
                                                <span>
                                                    <IconButton
                                                        size='small'
                                                        onClick={() => onEdit(attempt)}
                                                        sx={{
                                                            padding: '4px',
                                                            color: editable ? 'primary.main' : 'grey.400'
                                                        }}
                                                    >
                                                        <Pencil className='w-3.5 h-3.5' />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        )}
                                    </div>
                                </TableCell>
                            </TableRow>
                        )
                    })}
                    {attempts.length === 0 && (
                        <TableRow>
                            <TableCell colSpan={5} align='center'>
                                {t`No contact attempts recorded`}
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
        </TableContainer>
    )
}
